import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "min-w-full" }
const _hoisted_4 = { class: "bg-gray-100" }
const _hoisted_5 = {
  scope: "col",
  class: "p-4 text-left text-sm font-semibold text-gray-800"
}
const _hoisted_6 = {
  scope: "col",
  class: "p-4 text-left text-sm font-semibold text-gray-800"
}
const _hoisted_7 = { class: "divide-y divide-gray-200" }
const _hoisted_8 = { class: "whitespace-nowrap p-4 text-sm font-medium text-gray-800" }
const _hoisted_9 = { class: "whitespace-nowrap p-4 text-sm font-medium text-gray-800" }
const _hoisted_10 = ["checked", "onChange"]
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "whitespace-nowrap p-4 text-center text-sm text-gray-500" }

import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { LockClosedIcon, PencilIcon } from '@heroicons/vue/solid';

import store from '@/store';
import { useUiStore, useAuraStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import RoleService from '@/services/api/RoleService';
import PermissionService from '@/services/api/PermissionService';

import {
    RoleModel,
    PermissionModel,
    Permission,
    PermissionGetters,
    PermissionActions,
} from '@/types';

const perPage = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'PermissionPage',
  setup(__props) {

const roles: Ref<RoleModel[]> = ref([]);
const permissions: Ref<PermissionModel[]> = ref([]);
const orderBy: Ref<string> = ref('permission.description');
const search = computed<string>({
    get: () => store.getters[PermissionGetters.SEARCH],
    set: (search: string) => store.dispatch(PermissionActions.SET_SEARCH, search),
});

const currentPage = computed<number>({
    get: () => store.getters[PermissionGetters.PAGE],
    set: (page: number) => store.dispatch(PermissionActions.SET_PAGE, page),
});

const descriptionalizedPermissions = computed<PermissionModel[]>(() => {
    return permissions.value.map((p) => {
        p.description = t('permission.permissions.' + p.id);
        return p;
    });
});

const AURA_PERMISSION_IDS = [
    'viewAura',
    'createAuraConversation',
    'viewAuraConversation',
    'viewAuraProcessingCosts',
    'deleteAuraConversation',
];

const filteredPermissions = computed<PermissionModel[]>(() => {
    return descriptionalizedPermissions.value.filter((p) => {
        // Hide AURA permissions if level has no vertical linked
        if (auraStore.isAuraEnabled !== true && AURA_PERMISSION_IDS.includes(p.id)) {
            return false;
        }
        return !search.value || p.description?.toLowerCase().includes(search.value.toLowerCase());
    });
});

const paginatedPermissions = computed<PermissionModel[]>(() => {
    return filteredPermissions.value.slice(
        (currentPage.value - 1) * perPage,
        currentPage.value * perPage,
    );
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const uiStore = useUiStore();
const auraStore = useAuraStore();

watch([search, orderBy], () => {
    currentPage.value = 1;
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchRolesAndPermissions = async () => {
    try {
        await setLoading(true);
        const [roleResponse, permissionResponse] = await Promise.all([
            RoleService.getAll<RoleModel[]>(),
            PermissionService.getAll<PermissionModel[]>(),
        ]);

        roles.value = roleResponse.data;
        permissions.value = permissionResponse.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const handleAddRemovePermissionClick = async (role: RoleModel, permission: PermissionModel) => {
    try {
        setLoading(true);
        const permissionRole = permission.roles.find((r: RoleModel) => r.id == role.id);

        if (permissionRole) {
            await RoleService.removePermission(role.id, permission.id);
            permission.roles.splice(permission.roles.indexOf(permissionRole), 1);
        } else {
            await RoleService.addPermission(role.id, permission.id);
            permission.roles.push(role);
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (route.query.clearStateOnSetup) {
        search.value = '';
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    // Check AURA context for current level
    await auraStore.checkContext();

    fetchRolesAndPermissions();
});

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiPagination = _resolveComponent("UiPagination")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(LockClosedIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('core.entity.Permissions')), 1)
        ])
      ]),
      actions: _withCtx(() => [
        (_ctx.$can(_unref(Permission).CREATE_ROLE))
          ? (_openBlock(), _createBlock(_unref(RouterLink), {
              key: 0,
              to: {
                        name: 'CreateRole',
                    }
            }, {
              default: _withCtx(() => [
                _createVNode(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('permission.actions.newRole')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, {
      class: "px-4 py-2",
      "no-padding": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_UiListSearch, {
          modelValue: search.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event))
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { "no-padding": "" }, {
      default: _withCtx(() => [
        _createElementVNode("table", _hoisted_3, [
          _createElementVNode("thead", _hoisted_4, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", _hoisted_5, _toDisplayString(_ctx.$t('permission.labels.module')), 1),
              _createElementVNode("th", _hoisted_6, _toDisplayString(_ctx.$t('permission.labels.name')), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roles.value, (role) => {
                return (_openBlock(), _createElementBlock("th", {
                  key: role.id,
                  scope: "col",
                  class: "p-4 text-center text-sm font-semibold text-gray-800"
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.$can(_unref(Permission).VIEW_ROLE) ? _unref(RouterLink) : 'span'), {
                    to: {
                                    name: 'ViewRoleById',
                                    params: {
                                        id: role.id,
                                    },
                                },
                    class: "text-primary inline-flex items-center truncate text-sm font-medium"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(role.name) + " ", 1),
                      (_ctx.$can(_unref(Permission).VIEW_ROLE))
                        ? (_openBlock(), _createBlock(_unref(PencilIcon), {
                            key: 0,
                            class: "ml-1 h-4 w-4",
                            "aria-hidden": "true"
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1032, ["to"]))
                ]))
              }), 128))
            ])
          ]),
          _createElementVNode("tbody", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedPermissions.value, (permission) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: permission.id
              }, [
                _createElementVNode("td", _hoisted_8, _toDisplayString(_ctx.$t(`permission.modules.${permission.module}`)), 1),
                _createElementVNode("td", _hoisted_9, _toDisplayString(permission.description), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roles.value, (role) => {
                  return (_openBlock(), _createElementBlock("td", {
                    key: role.id,
                    class: "whitespace-nowrap p-4 text-center text-sm text-gray-500"
                  }, [
                    _withDirectives(_createElementVNode("input", {
                      type: "checkbox",
                      checked: 
                                    permission.roles.find((r) => r.id == role.id)
                                        ? true
                                        : false
                                ,
                      onChange: ($event: any) => (handleAddRemovePermissionClick(role, permission))
                    }, null, 40, _hoisted_10), [
                      [_directive_tippy, {
                                    content: permission.description,
                                    placement: 'left',
                                }]
                    ])
                  ]))
                }), 128))
              ]))
            }), 128)),
            (!paginatedPermissions.value.length)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_11, [
                  _createElementVNode("td", _hoisted_12, _toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPagination, {
      modelValue: currentPage.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentPage).value = $event)),
      count: filteredPermissions.value.length,
      perPage: perPage
    }, null, 8, ["modelValue", "count"])
  ]))
}
}

})